import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { PermissionsService } from 'src/app/services/permissions.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-instructor-toggle',
  templateUrl: './instructor-toggle.component.html',
  styleUrls: ['./instructor-toggle.component.css']
})
export class InstructorToggleComponent implements OnInit, OnChanges {
  @Input() instructors: { id: string, title: string, pinned?: boolean }[] = [];
  @Input() mobileMode = false;
  @Input() selectedInstructorId: string | null = null;
  @Input() storageScope: string | null = null;
  @Output() instructorSelectionChanged = new EventEmitter<string[]>();
  @Output() instructorSelected = new EventEmitter<string>();

  // Track selected instructor IDs
  selectedInstructors: string[] = [];

  // Storage key base for persisting instructor visibility
  private readonly STORAGE_BASE_KEY = 'dayview_instructor_visibility';

  // Compute the effective storage key, scoping it per studio when provided
  private get storageKey(): string {
    const scope = this.storageScope?.toString().trim();
    return scope ? `${this.STORAGE_BASE_KEY}:${scope}` : this.STORAGE_BASE_KEY;
  }

  constructor(public _permissionsService: PermissionsService) { }

  ngOnInit(): void {
    // Load stored instructor selections if available
    this.loadStoredSelections();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const storageScopeChanged = changes['storageScope'] && !changes['storageScope'].firstChange;

    if (storageScopeChanged) {
      this.selectedInstructors = [];
    }

    // When instructors are loaded/changed, update selected instructors
    if (changes['instructors'] && this.instructors && this.instructors.length > 0) {
      if (this.mobileMode) {
        this.selectedInstructorId = this.selectedInstructorId || this.instructors[0]?.id || null;
        this.emitSelectedInstructor();
      } else {
        const firstChange = changes['instructors'].firstChange;
        const prevLength = changes['instructors'].previousValue?.length || 0;
        const currLength = this.instructors.length;

        // Initialize selections if it's the first change, a significant change in count,
        // or if no instructors are currently selected
        if (firstChange || Math.abs(prevLength - currLength) >= 1 || this.selectedInstructors.length === 0) {
          // Load stored selections or default to all instructors
          const storedSelections = this.loadStoredSelections();
          if (storedSelections.length > 0) {
            // Validate stored selections against current instructors
            const validInstructorIds = new Set(this.instructors.map(i => i.id));
            this.selectedInstructors = storedSelections.filter(id => validInstructorIds.has(id));

            // If no stored selections are valid, select all
            if (this.selectedInstructors.length === 0) {
              this.selectedInstructors = this.instructors.map(instructor => instructor.id);
            }
          } else {
            this.selectedInstructors = this.instructors.map(instructor => instructor.id);
          }

          // Pinned columns (the Website Booking tray) start visible whatever
          // was stored; unticking one lasts for the session only.
          this.instructors
            .filter(instructor => instructor.pinned && !this.selectedInstructors.includes(instructor.id))
            .forEach(instructor => this.selectedInstructors.push(instructor.id));

          if (firstChange || prevLength === 0) {
            this.emitSelectionChange();
          }
        }
      }
    }

    if (changes['selectedInstructorId'] && this.mobileMode) {
      this.emitSelectedInstructor();
    }
  }

  /**
   * Toggle an instructor's selection status
   * @param instructorId ID of the instructor to toggle
   * @param event Optional MatButtonToggleChange event
   */
  toggleInstructor(instructorId: string, event?: any): void {
    if (this.mobileMode) {
      this.selectedInstructorId = instructorId;
      this.emitSelectedInstructor();
      return;
    }

    const index = this.selectedInstructors.indexOf(instructorId);

    // If the event is provided, use its checked status
    if (event && typeof event.checked === 'boolean') {
      if (event.checked && index === -1) {
        // Add to selected list
        this.selectedInstructors.push(instructorId);
      } else if (!event.checked && index !== -1) {
        // Remove from selected list
        this.selectedInstructors.splice(index, 1);
      }
    } else {
      // Traditional toggle behavior for backward compatibility
      if (index === -1) {
        // If not selected, add to selected list
        this.selectedInstructors.push(instructorId);
      } else {
        // If already selected, remove from selected list
        this.selectedInstructors.splice(index, 1);
      }
    }

    // Notify parent of the change
    this.emitSelectionChange();
  }

  isSelected(instructorId: string): boolean {
    return this.selectedInstructors.includes(instructorId);
  }

  emitSelectionChange(): void {
    // Save to storage whenever selections change
    this.saveSelections();
    this.instructorSelectionChanged.emit(this.selectedInstructors);
  }

  emitSelectedInstructor(): void {
    if (!this.mobileMode) {
      return;
    }
    if (this.selectedInstructorId) {
      this.instructorSelected.emit(this.selectedInstructorId);
    }
  }

  selectAll(): void {
    this.selectedInstructors = this.instructors.map(instructor => instructor.id);
    this.emitSelectionChange();
  }

  deselectAll(): void {
    this.selectedInstructors = [];
    this.emitSelectionChange();
  }

  /**
   * Load instructor selections from sessionStorage
   * @returns Array of stored instructor IDs
   */
  private loadStoredSelections(): string[] {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        LoggerService.log('[INSTRUCTOR-TOGGLE] Loaded stored selections:', parsed);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      LoggerService.error('[INSTRUCTOR-TOGGLE] Error loading stored selections:', error);
    }
    return [];
  }

  /**
   * Save current instructor selections to sessionStorage
   */
  private saveSelections(): void {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.selectedInstructors));
      LoggerService.log('[INSTRUCTOR-TOGGLE] Saved selections:', this.selectedInstructors);
    } catch (error) {
      LoggerService.error('[INSTRUCTOR-TOGGLE] Error saving selections:', error);
    }
  }
}
